/**
 * Клиент Битрикс24 для встроенного режима (iframe placement).
 *
 * Использует BX24 JS SDK (подключается в placement-странице сервером, см.
 * server/src/placement.ts). SDK позволяет вызывать REST-методы ОТ ИМЕНИ
 * текущего пользователя прямо из браузера — без вебхука и без отдельного
 * OAuth-сервера. Права пользователя Битрикса соблюдаются автоматически.
 *
 * Вне Битрикса (standalone, window.BX24 отсутствует) — функции сообщают, что
 * интеграция недоступна, и приложение работает на демо-данных (seed).
 */
import type { Employee } from '@/types';

// ── Типы BX24 SDK (минимально необходимое) ─────────────────
interface BX24Result {
  data(): any;
  error(): unknown | false;
  more(): boolean;
  next(): void;
  total(): number;
}
interface BX24Static {
  init(cb: () => void): void;
  callMethod(method: string, params: Record<string, unknown>, cb: (res: BX24Result) => void): void;
  getAuth(): { access_token: string; domain: string; member_id: string } | false;
  fitWindow?(): void;
  resizeWindow?(w: number, h: number): void;
  placement?: { info(): { placement: string; options: Record<string, any> } | null };
}

declare global {
  interface Window {
    BX24?: BX24Static;
    __BITRIX_EMBED__?: boolean;
  }
}

/** Открыты ли мы внутри Битрикса (есть SDK и флаг встраивания). */
export function isInBitrix(): boolean {
  return typeof window !== 'undefined' && !!window.BX24;
}

/**
 * Текущая BX24-авторизация (домен портала + access_token) для серверной проверки
 * личности на /api/auth/bitrix. Требует уже выполненного initBitrix().
 */
export function getBitrixAuth(): { domain: string; access_token: string } | null {
  if (!isInBitrix()) return null;
  const auth = window.BX24!.getAuth();
  if (!auth || !auth.access_token || !auth.domain) return null;
  return { domain: auth.domain, access_token: auth.access_token };
}

/**
 * Контекст размещения (placement) из Битрикса: где открыто приложение и с какими
 * параметрами. В карточке сделки `placement` = 'CRM_DEAL_DETAIL_TAB', а в options —
 * ID сделки (`ID` / `ENTITY_VALUE_ID`). В левом меню — 'DEFAULT' без ID.
 * Требует уже выполненного initBitrix().
 */
export function getPlacementInfo(): { placement: string; options: Record<string, any> } | null {
  if (!isInBitrix() || !window.BX24!.placement) return null;
  try { return window.BX24!.placement.info() ?? null; } catch { return null; }
}

/** Если приложение открыто из карточки сделки — вернуть ID сделки Битрикса, иначе null. */
export function getPlacementDealId(): string | null {
  const info = getPlacementInfo();
  if (!info || !/DEAL_DETAIL/i.test(info.placement)) return null;
  const raw = info.options?.ID ?? info.options?.ENTITY_VALUE_ID ?? info.options?.id;
  return raw != null && String(raw).trim() ? String(raw).trim() : null;
}

let initPromise: Promise<void> | null = null;
let autoFitStarted = false;

/**
 * Подгоняет высоту iframe-плейсмента под реальную высоту содержимого — без этого
 * Битрикс держит фиксированную маленькую высоту фрейма (видно, например, на экране
 * входа, где контента мало, и на каждом экране с другой высотой). ResizeObserver
 * ловит ЛЮБОЕ изменение высоты body (переход между экранами, раскрытие списков,
 * открытие модалок) и каждый раз просит Битрикс подстроиться заново.
 */
function startAutoFit(): void {
  if (autoFitStarted || !window.BX24?.fitWindow) return;
  autoFitStarted = true;
  const fit = () => { try { window.BX24!.fitWindow!(); } catch { /* не внутри плейсмента — игнорируем */ } };
  fit();
  const ro = new ResizeObserver(() => fit());
  ro.observe(document.documentElement);
}

/** Инициализация SDK (один раз). Резолвится, когда BX24 готов к вызовам. */
export function initBitrix(): Promise<void> {
  if (!isInBitrix()) return Promise.reject(new Error('BX24 SDK недоступен (не внутри Битрикса)'));
  if (initPromise) return initPromise;
  initPromise = new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('BX24.init: таймаут')), 15000);
    try {
      window.BX24!.init(() => {
        clearTimeout(timer);
        startAutoFit();
        resolve();
      });
    } catch (e) {
      clearTimeout(timer);
      reject(e);
    }
  });
  return initPromise;
}

/**
 * Вызов REST-метода с автоматической постраничной подгрузкой (start/next).
 * Аккумулирует все страницы результата в массив.
 */
export function callMethodAll<T = any>(method: string, params: Record<string, unknown> = {}): Promise<T[]> {
  return new Promise<T[]>((resolve, reject) => {
    if (!isInBitrix()) return reject(new Error('BX24 SDK недоступен'));
    const acc: T[] = [];
    const handler = (res: BX24Result) => {
      const err = res.error();
      if (err) return reject(new Error(typeof err === 'object' ? JSON.stringify(err) : String(err)));
      const data = res.data();
      if (Array.isArray(data)) acc.push(...data);
      else if (data != null) acc.push(data);
      if (res.more()) res.next();
      else resolve(acc);
    };
    window.BX24!.callMethod(method, params, handler);
  });
}

// ── Сырой пользователь Битрикса (нужные поля) ──────────────
interface BitrixUser {
  ID: string;
  NAME?: string;
  LAST_NAME?: string;
  SECOND_NAME?: string;
  WORK_POSITION?: string;
  EMAIL?: string;
  PERSONAL_MOBILE?: string;
  WORK_PHONE?: string;
  ACTIVE?: boolean | string;
  UF_DEPARTMENT?: number[];
}

/** Собрать ФИО из частей пользователя Битрикса. */
function buildFullName(u: BitrixUser): string {
  const parts = [u.LAST_NAME, u.NAME, u.SECOND_NAME].filter((p) => !!p && String(p).trim());
  const name = parts.join(' ').trim();
  return name || `Сотрудник #${u.ID}`;
}

/**
 * Загрузить сотрудников портала через user.get и привести к модели Employee.
 * Только активные пользователи. branch_id пока не маппится (филиалы —
 * отдельный этап через UF_DEPARTMENT/department.get).
 */
export async function fetchEmployees(): Promise<Employee[]> {
  const users = await callMethodAll<BitrixUser>('user.get', { FILTER: { ACTIVE: true }, SORT: 'LAST_NAME', ORDER: 'ASC' });
  return users
    .filter((u) => u && u.ID)
    .map((u): Employee => ({
      id: `bx-${u.ID}`,
      full_name: buildFullName(u),
      bitrix_user_id: Number(u.ID),
      position: u.WORK_POSITION?.trim() || undefined,
      email: u.EMAIL?.trim() || undefined,
      phone: (u.PERSONAL_MOBILE || u.WORK_PHONE)?.trim() || undefined,
      branch_id: null,
      is_active: u.ACTIVE === true || u.ACTIVE === 'Y' || u.ACTIVE === undefined,
      bitrix_entity: 'USER',
      sync_state: 'synced',
      synced_at: Date.now(),
    }));
}

/** Текущий пользователь (для индикатора подключения). */
export async function fetchCurrentUser(): Promise<{ name: string; id: number } | null> {
  const res = await callMethodAll<BitrixUser>('user.current', {});
  const u = res[0];
  if (!u) return null;
  return { name: buildFullName(u), id: Number(u.ID) };
}
